import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import { toSourceSearchTableState } from "@/lib/search/sourceSearch";
import { useLegislationTableStore } from "@/stores/legislation-table-state.store";
import { parseSearchUrlParams } from "@/utils/searchParamsFormatter";

/** The params a stored source search carries — see `buildSourceSearchUrl`. */
const SOURCE_SEARCH_PARAMS = ["searchQuery", "searchTags", "filters"] as const;

export interface SourceSearchLink {
  /** True while the link is being applied and the table must not render yet. */
  isApplyingLink: boolean;
  /**
   * Changes every time a link is applied. The Kilder table mirrors the stored
   * search term in local state that is seeded on mount only, so it has to be
   * keyed on this to show a search restored while it was already open.
   */
  tableKey: number;
}

/**
 * Opens a source search that arrives as a link on the Kilder page — from
 * recent searches, a favorite search card, or a shared link — by applying it
 * to the table state and then stripping it from the URL (LAR-422).
 */
export const useSourceSearchLink = (): SourceSearchLink => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [appliedLinkCount, setAppliedLinkCount] = useState(0);

  const isApplyingLink = SOURCE_SEARCH_PARAMS.some((param) =>
    searchParams.has(param)
  );

  useEffect(() => {
    if (!isApplyingLink) return;

    const stored = parseSearchUrlParams(
      `${AppRoutes.legislations}?${searchParams.toString()}`
    );
    const restored = toSourceSearchTableState(stored);

    useLegislationTableStore.getState().setTableState({
      search: restored.search,
      searchTags: restored.searchTags,
      columnFilters: restored.columnFilters,
      data: [],
      pageIndex: 0,
      pageCount: 0,
      sorting: [],
    });

    setAppliedLinkCount((count) => count + 1);

    const remainingParams = new URLSearchParams(searchParams);
    for (const param of [...SOURCE_SEARCH_PARAMS, "tab"]) {
      remainingParams.delete(param);
    }
    setSearchParams(remainingParams, { replace: true });
  }, [isApplyingLink, searchParams, setSearchParams]);

  return { isApplyingLink, tableKey: appliedLinkCount };
};
